'use client';

/**
 * **The attribute treatment's row, as three pieces an inventory surface can
 * compose** (LF-6314).
 *
 * The treatment's *grid* already exists in production (`attribute-grid.tsx`,
 * `contact-attribute-grid.tsx`), but it is spec-driven: it renders a
 * `AttributeSpec[]` through its own read-out/editor/commit machinery. Three things
 * on the inventory drawer are the treatment's **row** without being one of its
 * *fields* —
 *
 * · a money figure whose typing behaviour belongs to `FormField` (`./currency-input`),
 * · a confirmation that belongs to a *figure* rather than to the record, and whose
 *   `Yes` reveals two more controls,
 * · an instruction whose value is a set of pills and a beneficiary allocation,
 *
 * — and each needs the label column, the value cell and the row rhythm without
 * inheriting a commit path it does not want. This is that vocabulary, so the three
 * land on one column instead of each composing `ATTRIBUTE_ROW` + `ATTRIBUTE_LABEL`
 * by hand and drifting a token at a time.
 *
 * Every measurement is imported from `components/attributes/attribute-tokens` — the
 * treatment's rules in executable form, whose own contract is that a new surface
 * *imports* them rather than retyping them. (LF-6388 / [ATTR-10] moved the shared
 * form system there from `components/leads/`; import it as
 * `@/components/attributes`, never a deep path.)
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  ATTRIBUTE_CELL,
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_EDITOR_CHROME,
  ATTRIBUTE_HOVER,
  ATTRIBUTE_INSET,
  ATTRIBUTE_LABEL,
  ATTRIBUTE_LEADING,
  ATTRIBUTE_MUTED_INK,
  ATTRIBUTE_RADIUS,
  ATTRIBUTE_ROW,
  ATTRIBUTE_VALUE_INK,
  ATTRIBUTE_VALUE_SLOT,
  ATTRIBUTE_VALUE_TEXT
} from '@/components/attributes';

/**
 * **Where a hint, an error or a helper line goes: under the value, not under the
 * label.**
 *
 * The label column plus the row's own `gap-4`, read from the custom property the
 * column is published through — so it tracks `ATTRIBUTE_LABEL_WIDTH` (a container
 * query, i.e. a different number at every drawer width) rather than freezing one
 * of its answers. Written out as a whole utility because Tailwind scans source
 * text and an interpolated arbitrary value is not one.
 */
export const ATTRIBUTE_VALUE_OFFSET =
  'ml-[calc(var(--attr-label-w)+4*var(--spacing))]';

/**
 * One row: the shared label column, and whatever the caller puts in the value
 * slot beside it.
 */
export function AttributeRow({
  label,
  required = false,
  children,
  className
}: {
  /**
   * `null` omits the label column, for a row that spans the width. `title` is set
   * from it so a label the 101px column has to ellipsise is still readable on
   * hover.
   */
  label: string | null;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn(ATTRIBUTE_ROW, className)}>
      {label !== null && (
        <span className={cn(ATTRIBUTE_LABEL, ATTRIBUTE_LEADING)} title={label}>
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </span>
      )}
      {children}
    </div>
  );
}

/**
 * The value cell — the hover target, the editor's chrome and the focus state in
 * one box, which is what puts the ring *around* the value and its affordances
 * rather than around the input inside it (see `ATTRIBUTE_EDITOR_CHROME` on why it
 * is a ring and not a border: a border adds a pixel, so opening a field nudged its
 * value 1px right and 1px down).
 */
export function AttributeCell({
  error = false,
  children,
  className
}: {
  error?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        ATTRIBUTE_CELL,
        ATTRIBUTE_EDITOR_CHROME,
        error && 'ring-destructive focus-within:ring-destructive',
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * A value slot holding a **live control** rather than an editor — a segmented
 * boolean, a set of pills — so it gets the row's inset and leading without the
 * editor's ring and fill. The same composition `attribute-boolean.tsx` uses for
 * exactly this case.
 */
export function AttributeControlSlot({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'items-center',
        ATTRIBUTE_VALUE_SLOT,
        ATTRIBUTE_INSET,
        ATTRIBUTE_LEADING,
        className
      )}
    >
      {children}
    </div>
  );
}

/**
 * **A value at rest that becomes an editor where it sits** — the treatment's
 * central motion, for a field that is not one of the grid's rows (round 6).
 *
 * Until now a compact field was permanently an open editor: a ring and a fill,
 * beside grid rows showing bare read-outs. Recorded in the entry as the known
 * remaining seam, and review's answer was that the balances should be *the same
 * fields* as the rows around them. This is that: at rest a plain value on the
 * whole-cell hover target, an editor in the same box on click.
 *
 * ## What it deliberately does not change: when the value is saved
 *
 * On the grid, closing an editor **commits**. Here it does not, and must not: these
 * fields live inside a form with a Save button, and the value has already reached
 * the form's state through `onChange` on every keystroke. So opening and closing is
 * *presentation* — nothing is written on blur and nothing is lost by it.
 *
 * That difference is the reason this was left open rather than guessed at, and it
 * is still the roll-out's decision: a surface that looks like commit-on-blur and
 * saves on a button is a milder inconsistency than two visual languages in one
 * panel, but it is not nothing. It is recorded in the story entry.
 *
 * ## Closing, and the one editor that must own it
 *
 * By default `blur` closes, with a `relatedTarget` check so moving between the
 * editor's own parts does not shut it. **A control that portals — a `Select`, whose
 * list is rendered outside this DOM subtree — must set `closeOnBlur={false}` and
 * call the `close` it is handed.**
 *
 * That is not a tidiness preference; it is the bug it was written for. With blur
 * closing, opening a portalled list moves focus *out* of this box, `relatedTarget`
 * is in the portal so `contains()` is false, and the cell closes — unmounting the
 * `Select` and taking the list with it. Reported as *"can only choose the first
 * option, or it's closing"*, and the "sometimes the first one works" part is the
 * tell: it is a race between the blur and the list mounting, so whichever happens
 * to win decides whether the click lands.
 */
export function AttributeEditableCell({
  /** What the value reads as at rest. Empty renders the `Set <Label>…` placeholder. */
  display,
  placeholder,
  error = false,
  /**
   * `false` for an editor that portals (a `Select`): blur cannot distinguish "opened
   * my own list" from "clicked away", so the editor closes the cell itself. See the
   * header — this is the difference between a working dropdown and one that shuts
   * the moment it opens.
   */
  closeOnBlur = true,
  renderEditor,
  /**
   * Revealed on hover beside the value, out of flow — a clear affordance, say. The
   * cell reserves room for it permanently, so revealing it never reflows the value
   * (which on a wrapping value would re-lay out the whole paragraph).
   */
  affordance
}: {
  display: string;
  placeholder: string;
  error?: boolean;
  closeOnBlur?: boolean;
  renderEditor: (close: () => void) => React.ReactNode;
  affordance?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const empty = display.trim() === '';

  if (!open) {
    return (
      <div
        className={cn(
          ATTRIBUTE_CELL,
          'relative',
          error && 'ring-destructive ring-1'
        )}
      >
        <button
          type="button"
          onClick={() => setOpen(true)}
          onFocus={() => setOpen(true)}
          title={empty ? undefined : display}
          className={cn(
            'min-w-0 flex-1 cursor-pointer text-left',
            ATTRIBUTE_RADIUS,
            ATTRIBUTE_INSET,
            ATTRIBUTE_VALUE_TEXT,
            ATTRIBUTE_LEADING,
            ATTRIBUTE_HOVER,
            empty ? cn('truncate', ATTRIBUTE_MUTED_INK) : ATTRIBUTE_VALUE_INK,
            // Room kept for the affordance whether or not it is showing.
            !!affordance && 'pr-6'
          )}
        >
          {empty ? placeholder : display}
        </button>
        {!!affordance && (
          <span className="absolute top-1 right-1 flex">{affordance}</span>
        )}
      </div>
    );
  }

  return (
    <div
      className={cn(
        ATTRIBUTE_CELL,
        ATTRIBUTE_EDITOR_CHROME,
        error && 'ring-destructive focus-within:ring-destructive'
      )}
      onBlur={
        closeOnBlur
          ? (event) => {
              // Focus moving *within* the editor is not leaving it.
              if (
                event.currentTarget.contains(event.relatedTarget as Node | null)
              ) {
                return;
              }
              close();
            }
          : undefined
      }
      onKeyDown={(event) => {
        if (event.key === 'Escape') close();
      }}
    >
      {renderEditor(close)}
    </div>
  );
}

/**
 * What a control inside {@link AttributeCell} wears: no chrome of its own, the
 * value's type, the shared inset and the shared leading.
 *
 * `ATTRIBUTE_VALUE_TEXT` sits **before** `ATTRIBUTE_LEADING` deliberately —
 * `lib/utils.ts` registers `text-<N>px` in tailwind-merge's `font-size` group,
 * which conflicts with `leading`, so the other order silently discards the leading
 * and the row loses 2.5px. Documented under `ATTRIBUTE_LEADING`; this is the live
 * trap it warns about.
 */
export const ATTRIBUTE_BARE_CONTROL = cn(
  ATTRIBUTE_EDITOR_BARE,
  ATTRIBUTE_INSET,
  ATTRIBUTE_VALUE_TEXT,
  ATTRIBUTE_LEADING,
  'h-auto min-w-0 flex-1 shadow-none'
);
